import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRbacError } from '@entities/withdrawal';
import type { ApproveWithdrawalPayload } from '@shared/api/contracts';
import { readApiError } from '@shared/api/errors/read-api-error';
import { useApiServices } from '@shared/lib/use-api-services';

export const useWithdrawalActions = () => {
  const { t } = useTranslation('cabinet');
  const { t: tRbac } = useTranslation('rbac');
  const services = useApiServices();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelWithdrawal = async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await services.withdrawals.cancelWithdrawal(id);
      return { success: true as const };
    } catch (err: unknown) {
      const changed = readApiError(err)?.code === 'WITHDRAWAL_NOT_CANCELLABLE';
      const message = changed
        ? t('withdrawals.actions.stateChanged')
        : formatRbacError(err, tRbac, t('withdrawals.actions.cancelError'));
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const processAdminWithdrawal = async (id: string, payload: ApproveWithdrawalPayload = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      await services.withdrawals.processAdminWithdrawal(id, payload);
      return { success: true as const };
    } catch (err: unknown) {
      const message = formatRbacError(err, tRbac, t('withdrawals.actions.processError'));
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const cancelAdminWithdrawal = async (id: string, comment: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await services.withdrawals.cancelAdminWithdrawal(id, { comment });
      return { success: true as const };
    } catch (err: unknown) {
      const message = formatRbacError(err, tRbac, t('withdrawals.actions.adminCancelError'));
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    cancelWithdrawal,
    processAdminWithdrawal,
    cancelAdminWithdrawal,
    isLoading,
    error,
  };
};
